import { useState } from "react"
import { router } from "@inertiajs/react"
import {
  ArrowLeftRight,
  Check,
  DollarSign,
  Gift,
  LifeBuoy,
  Pause,
  Play,
  Plus,
  RotateCcw,
  Star,
  X,
} from "lucide-react"

import { DialogClose, DialogFooter } from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { Textarea } from "@/components/ui/textarea"
import { Input } from "@/components/ui/input"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group"
import { Slider } from "@/components/ui/slider"
import { cn } from "@/lib/utils"
import { Shell, Note, Body, Field, WhoLine, Footer, BusyProvider, freelancerOf } from "./modalChrome"
import RequestRevisionDialog from "./RequestRevisionDialog"
import ReviewScanDialog from "./ReviewScanDialog"
import DiscountCodeBox from "./DiscountCodeBox"
import FreelancerRequestsDialog from "@/components/tuw/FreelancerRequestsDialog"
import { Elements, PaymentElement, useElements, useStripe } from "@stripe/react-stripe-js"
import { getStripe } from "@/lib/stripe"
import { createTipIntent, csrfToken } from "@/lib/orderActions"
import { ORDER_MODAL_KEYS } from "./actions"

// ── modals ───────────────────────────────────────────────────────────────────
// Shared chrome (Shell/Note/Body/Field/WhoLine/Footer) lives in modalChrome.jsx
// so a dialog can grow into its own file without importing from this registry.

function ApproveDraftDialog({ order, runAction, onClose }) {
  const sample = order.approvalFlow === "sample"

  return (
    <Shell title="Approve draft" onClose={onClose}>
      <Note tone="red">
        <b className="font-bold text-primary">Heads up —</b> no content changes can be made after you approve the draft.
      </Note>
      <Body>
        {sample
          ? "Approving locks the sample and sends it to originality review. After you approve the report, the sample is complete."
          : "Approving locks the manuscript and hands it to your editor to begin editing. You'll be notified when the edited version is ready for final review."}
      </Body>
      <Footer SubmitIcon={Check} submitLabel="Yes, approve draft" onSubmit={() => runAction("review", { approved: true })} />
    </Shell>
  )
}

function ApproveFinalDialog({ order, runAction, onClose }) {
  return (
    <Shell title="Approve & complete" onClose={onClose}>
      <Note tone="red">
        <b className="font-bold text-primary">This completes your order.</b> Once approved, the final files unlock for download and
        no further changes can be requested without a new revision.
      </Note>
      <Body>
        You&apos;re approving the finished <b className="font-semibold text-foreground">{order.title}</b>. We&apos;ll mark the project
        complete and notify your team. You can still tip and rate your freelancer afterward.
      </Body>
      <Footer SubmitIcon={Check} submitLabel="Approve & complete" onSubmit={() => runAction("review", { approved: true })} />
    </Shell>
  )
}

function ApproveDesignDialog({ order, runAction, data, onClose }) {
  const designer = freelancerOf(order)
  const isCover = order.type === "cover"
  const nums = (order.deliverables?.designs ?? []).map((d) => d.num)
  const options = nums.length ? nums : [1, 2, 3]
  const [concept, setConcept] = useState(data?.concept || order.deliverables?.approvedDesign || options[0])

  return (
    <Shell title={isCover ? "Approve cover design" : "Approve illustrations"} onClose={onClose}>
      <WhoLine person={designer} sub={`${designer.role || "Designer"} · ${order.title}`} />
      <Body>
        {isCover ? (
          <>Approving locks your selected concept as the final cover. Your designer will prepare print-ready paperback and eBook files.</>
        ) : (
          <>Approving locks this illustration set as final. Your illustrator will prepare the delivery files. Need a tweak first? Close this and request a revision instead.</>
        )}
      </Body>
      {isCover && (
        <Field label="Which concept are you approving?">
          <div className="flex gap-2">
            {options.map((n) => (
              <button
                key={n}
                type="button"
                onClick={() => setConcept(n)}
                className={cn(
                  "flex-1 rounded-[11px] border px-3 py-2.5 text-[13px] font-semibold transition-colors",
                  concept === n ? "border-primary bg-[var(--red-soft)] text-primary" : "border-border text-muted-foreground hover:bg-accent"
                )}
              >
                Concept {String.fromCharCode(64 + n)}
              </button>
            ))}
          </div>
        </Field>
      )}
      <Footer
        SubmitIcon={Check}
        submitLabel={isCover ? "Approve this cover" : "Approve illustrations"}
        onSubmit={() => runAction("review", isCover ? { approved: true, approvedDesignNum: concept } : { approved: true })}
      />
    </Shell>
  )
}

// The dollar figures (amountDue, pointDiscount) come pre-computed from the
// server payload — the client never converts points to dollars (P-001).
function money(value) {
  return `$${Number(value ?? 0).toFixed(2)}`
}

function PayNowDialog({ order, runAction, onClose }) {
  // The reward fields and server-authorized target (present while payable).
  // `pay` is refreshed WHOLESALE from each apply response — no field-picking, no
  // client recomputation (AQ-013, decision 8b/8c).
  const [pay, setPay] = useState(order.payment)
  // The TOTAL points the customer wants applied (absolute, not a delta), bounded
  // by the server-computed maxApplicablePoints consumed as-is.
  const [points, setPoints] = useState(order.payment?.pointsApplied ?? 0)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState(null)
  const [codeError, setCodeError] = useState(null)
  const target = pay?.target

  const isPrimary = pay && target?.kind === "primary"
  const canRedeem = isPrimary && pay.maxApplicablePoints > 0
  const dirty = pay && points !== pay.pointsApplied

  // Points and the discount code both answer with the full payment payload.
  const updatePayment = async (path, body, fail) => {
    setBusy(true)
    setError(null)
    setCodeError(null)
    try {
      const res = await fetch(`/projects/${order.id}/${path}`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json", "X-CSRF-Token": csrfToken() },
        body: JSON.stringify(body),
      })
      const data = await res.json().catch(() => ({}))
      if (!res.ok) {
        // A refused code still answers with the project's current payment state.
        if (data.payment) {
          setPay(data.payment)
          setPoints(data.payment.pointsApplied)
        }
        throw new Error(data.error)
      }

      // Replace the entire payment state from the response, then re-render bounds
      // and totals from it.
      setPay(data)
      setPoints(data.pointsApplied)

      // paid: true is terminal success (full coverage): the invoice is already
      // paid, so skip the pay step and reload into the paid state.
      if (data.paid) router.reload()
    } catch (e) {
      fail(e.message)
    } finally {
      setBusy(false)
    }
  }

  const applyPoints = (total) =>
    updatePayment("reward_points", { amount: total }, (message) =>
      setError(message || "We couldn't update your reward points. Please try again.")
    )

  const applyCode = (code) =>
    updatePayment("discount_code", { code }, () => setCodeError("That code could not be applied."))

  return (
    <Shell title="Pay now" onClose={onClose}>
      <Body>
        You&apos;re paying for <b className="font-semibold text-foreground">{target?.label || "this project"}</b> on{" "}
        <b className="font-semibold text-foreground">{order.title}</b>. The amount due is{" "}
        <b className="font-semibold text-foreground">{money(pay?.amountDue)}</b>. You&apos;ll complete payment at our secure checkout.
      </Body>

      {isPrimary && (
        <DiscountCodeBox
          key={pay.discount?.code ?? ""}
          discount={pay.discount}
          updatable={pay.discountCodeUpdatable}
          busy={busy}
          error={codeError}
          onApply={applyCode}
          onRemove={() => applyCode("")}
        />
      )}

      {canRedeem ? (
        <div className="flex flex-col gap-[13px] rounded-[11px] border border-border bg-accent px-[15px] py-[14px]">
          <div className="flex items-center gap-[7px] text-[12px] font-bold text-foreground">
            <Gift className="size-[15px] text-primary" strokeWidth={2} />
            Apply your reward points
          </div>

          <Field label={`Points to apply — ${points.toLocaleString("en-US")} of ${pay.maxApplicablePoints.toLocaleString("en-US")}`}>
            <Slider
              value={[Math.min(points, pay.maxApplicablePoints)]}
              min={0}
              max={pay.maxApplicablePoints}
              onValueChange={(v) => setPoints(v[0])}
            />
          </Field>

          <div className="flex items-center justify-between text-[12.5px]">
            <span className="text-muted-foreground">Points discount</span>
            <b className="font-bold text-done">−{money(pay.pointDiscount)}</b>
          </div>
          <div className="flex items-center justify-between text-[13px]">
            <span className="font-semibold text-foreground">Amount due</span>
            <b className="font-display text-[15px] font-bold text-foreground">{money(pay.amountDue)}</b>
          </div>

          <Button
            variant="outline"
            className="gap-[7px] font-bold"
            disabled={busy || !dirty}
            onClick={() => applyPoints(points)}
          >
            {busy ? "Applying…" : "Apply points"}
          </Button>
        </div>
      ) : (
        <Note tone="muted">Your itemised total, discounts and reward points are shown on the checkout page.</Note>
      )}

      {error && <Note tone="red">{error}</Note>}

      <Footer
        SubmitIcon={DollarSign}
        submitLabel="Continue to payment"
        disabled={busy || !target}
        onSubmit={() => runAction("pay", { targetKind: target.kind, targetId: target.id })}
      />
    </Shell>
  )
}

function HoldDialog({ order, runAction, onClose }) {
  const [duration, setDuration] = useState(5)
  const [notes, setNotes] = useState("")

  return (
    <Shell title="Project hold request" onClose={onClose}>
      <Body>
        This sends a hold request to your freelancer. Once approved, the project is placed <b className="font-semibold text-foreground">On Hold</b>{" "}
        for the duration requested and resumes automatically when the hold ends.
      </Body>
      <Field label="Hold duration (days)" htmlFor="hold-duration">
        <Input
          id="hold-duration"
          type="number"
          min={1}
          max={10}
          value={duration}
          onChange={(e) => setDuration(e.target.value)}
        />
      </Field>
      <Field label="Reason for hold" htmlFor="hold-notes">
        <Textarea id="hold-notes" rows={4} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Let your team know why you're pausing…" />
      </Field>
      <Note tone="muted">
        <b className="font-bold text-foreground">Please note —</b> two holds are permitted per order, totalling a max of 10 days.
        If the freelancer doesn&apos;t respond within 48 hours, the hold begins automatically.
      </Note>
      <Footer SubmitIcon={Pause} submitLabel="Send hold request" onSubmit={() => runAction("hold", { duration, notes })} />
    </Shell>
  )
}

function ReallocateDialog({ order, runAction, onClose }) {
  const [reason, setReason] = useState("Communication issues")
  const [note, setNote] = useState("")

  return (
    <Shell title="Reallocate order" onClose={onClose}>
      <Note tone="muted">
        <b className="font-bold text-foreground">Please note —</b> this won&apos;t automatically reassign your project. A message is
        sent to your manager, who will reply within one business day.
      </Note>
      <Field label="Reason for reallocation">
        <Select value={reason} onValueChange={setReason}>
          <SelectTrigger className="w-full">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="Communication issues">Communication issues</SelectItem>
            <SelectItem value="Missed deadlines">Missed deadlines</SelectItem>
            <SelectItem value="Style isn't the right fit">Style isn&apos;t the right fit</SelectItem>
            <SelectItem value="Other — reason in notes">Other — reason in notes</SelectItem>
          </SelectContent>
        </Select>
      </Field>
      <Field label="Note (optional)" htmlFor="realloc-note">
        <Textarea id="realloc-note" rows={4} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Add any context for your manager…" />
      </Field>
      <Footer SubmitIcon={ArrowLeftRight} submitLabel="Request reallocation" onSubmit={() => runAction("reallocate", { reason, note })} />
    </Shell>
  )
}

const TIP_PRESETS = [
  { amount: 25, label: "Thanks" },
  { amount: 50, label: "Generous" },
  { amount: 100, label: "Wow" },
]

// The card step: Stripe's PaymentElement, mounted inside an <Elements> provider
// carrying the PaymentIntent's clientSecret and confirmed in the browser. On
// success Stripe redirects to return_url on the order page (where OrderDetail
// shows the thank-you); the Stripe webhook records the tip server-side.
function TipCardForm({ amount, order }) {
  const stripe = useStripe()
  const elements = useElements()
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState(null)

  const pay = async () => {
    if (!stripe || !elements) return
    setSubmitting(true)
    setError(null)

    const tab = new URLSearchParams(window.location.search).get("tab") || ""
    const returnUrl = `${window.location.origin}/projects/${order.id}?tab=${tab}&tip=success`

    const { error: stripeError } = await stripe.confirmPayment({
      elements,
      confirmParams: { return_url: returnUrl },
    })

    // We only land here on an immediate error; success redirects to return_url.
    if (stripeError) {
      setError(stripeError.message)
      setSubmitting(false)
    }
  }

  return (
    <div className="flex flex-col gap-4">
      <PaymentElement />
      {error && <Note tone="red">{error}</Note>}
      <DialogFooter>
        <DialogClose asChild>
          <Button variant="outline">Cancel</Button>
        </DialogClose>
        <Button className="gap-[7px] font-bold" onClick={pay} disabled={!stripe || submitting}>
          <Gift className="size-4" strokeWidth={2.1} />
          {submitting ? "Processing…" : `Pay $${amount}`}
        </Button>
      </DialogFooter>
    </div>
  )
}

function TipDialog({ order, onClose }) {
  // The eligible recipients (writer/editor pairing) surfaced explicitly by the
  // server, never inferred from team-array order (AQ-016). Default to the single
  // eligible freelancer; when multiple, the customer picks one.
  const recipients = order.tipRecipients?.length ? order.tipRecipients : [freelancerOf(order)]
  // Radix RadioGroup works in strings; keep the selection stringy and coerce at
  // submit (the backend find_by(id:) accepts the string id).
  const [recipientId, setRecipientId] = useState(
    recipients.length === 1 && recipients[0].userId != null ? String(recipients[0].userId) : null
  )
  const freelancer = recipients.find((r) => String(r.userId) === recipientId) ?? recipients[0]
  const multiple = recipients.length > 1
  const [amount, setAmount] = useState(50)
  const [clientSecret, setClientSecret] = useState(null)
  const [starting, setStarting] = useState(false)
  const [error, setError] = useState(null)

  const start = async () => {
    setStarting(true)
    setError(null)
    try {
      setClientSecret(await createTipIntent(order.id, amount, recipientId))
    } catch (e) {
      setError(e.message)
    } finally {
      setStarting(false)
    }
  }

  return (
    <Shell title="Tip your freelancer" onClose={onClose}>
      <WhoLine person={freelancer} sub={`${freelancer.role || "Freelancer"} · ${order.title}`} />

      {clientSecret ? (
        <>
          <Body>
            You&apos;re tipping <b className="font-semibold text-foreground">${amount}</b>. Enter your card below to send it to {freelancer.name}.
          </Body>
          <Elements
            stripe={getStripe()}
            options={{ clientSecret, appearance: { theme: "stripe", variables: { colorPrimary: "#ff1f4a" } } }}
          >
            <TipCardForm amount={amount} order={order} />
          </Elements>
        </>
      ) : (
        <>
          <Body>Show your appreciation for outstanding work. 100% of your tip goes directly to {freelancer.name}.</Body>
          {multiple && (
            <RadioGroup value={recipientId ?? ""} onValueChange={setRecipientId} className="mb-3 grid gap-2">
              {recipients.map((r) => (
                <label
                  key={r.userId}
                  className={cn(
                    "flex cursor-pointer items-center gap-2.5 rounded-[11px] border px-3 py-2.5 transition-colors",
                    recipientId === String(r.userId) ? "border-primary bg-[var(--red-soft)]" : "border-border hover:bg-accent"
                  )}
                >
                  <RadioGroupItem value={String(r.userId)} />
                  <span className="text-[13px] font-semibold">{r.name}</span>
                  <span className="ml-auto text-[11px] text-ink-3">{r.role}</span>
                </label>
              ))}
            </RadioGroup>
          )}
          <div className="flex gap-2">
            {TIP_PRESETS.map((preset) => (
              <button
                key={preset.amount}
                type="button"
                onClick={() => setAmount(preset.amount)}
                className={cn(
                  "flex-1 rounded-[11px] border px-3 py-3 text-center transition-colors",
                  amount === preset.amount ? "border-primary bg-[var(--red-soft)]" : "border-border hover:bg-accent"
                )}
              >
                <div className="font-display text-[19px] font-bold">${preset.amount}</div>
                <div className="mt-0.5 text-[10.5px] text-ink-3">{preset.label}</div>
              </button>
            ))}
          </div>
          {error && <Note tone="red">{error}</Note>}
          <Footer
            SubmitIcon={Gift}
            submitLabel={starting ? "Starting…" : `Continue to $${amount} tip`}
            disabled={starting || (multiple && !recipientId)}
            onSubmit={start}
          />
        </>
      )}
    </Shell>
  )
}

function ManageAccessDialog({ order, runAction, onClose }) {
  const [email, setEmail] = useState("")

  return (
    <Shell title="Manage order access" onClose={onClose}>
      <Note tone="muted">
        <b className="font-bold text-foreground">Please note —</b> this grants access to the <b className="font-semibold text-foreground">Document tab only</b>.
        Collaborators can read and comment, but can&apos;t approve, pay, or change the order.
      </Note>
      <Field label="Invite by email" htmlFor="access-email">
        <Input id="access-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="name@example.com" />
      </Field>
      <Footer SubmitIcon={Plus} submitLabel="Add access" disabled={!email} onSubmit={() => runAction("access", { email })} />
    </Shell>
  )
}

function ResumeDialog({ order, runAction, onClose }) {
  return (
    <Shell title="Resume project" onClose={onClose}>
      <Body>
        This project is currently <b className="font-semibold text-foreground">On Hold</b>. Resuming now lifts the hold early and asks
        your team to pick the work back up.
      </Body>
      <Note tone="muted">Due dates shift forward by the time already spent on hold. Your team is notified the moment you resume.</Note>
      <Footer SubmitIcon={Play} submitLabel="Resume now" onSubmit={() => runAction("resume", {})} />
    </Shell>
  )
}

function RateDialog({ order, runAction, onClose }) {
  // The server lists who the customer can still rate, writer first; with more
  // than one, the customer picks. `seat` tells the server which one.
  const targets = order.rateTargets?.length ? order.rateTargets : [freelancerOf(order)]
  const [seat, setSeat] = useState(targets[0].seat)
  const freelancer = targets.find((t) => t.seat === seat) ?? targets[0]
  const [score, setScore] = useState(5)
  const [notes, setNotes] = useState("")

  return (
    <Shell title="Leave a rating" onClose={onClose}>
      {targets.length > 1 ? (
        <Select value={seat} onValueChange={setSeat}>
          <SelectTrigger className="w-full" aria-label="Freelancer">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {targets.map((t) => (
              <SelectItem key={t.seat} value={t.seat}>
                {t.name} · {t.role}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      ) : (
        <WhoLine person={freelancer} sub={`${freelancer.role || "Freelancer"} · ${order.title}`} />
      )}
      <Field label="Your rating">
        <div className="flex gap-1.5">
          {[1, 2, 3, 4, 5].map((n) => (
            <button key={n} type="button" aria-label={`${n} star${n === 1 ? "" : "s"}`} onClick={() => setScore(n)}>
              <Star
                className="size-7"
                strokeWidth={1.6}
                style={{ color: "var(--amber)", fill: n <= score ? "var(--amber)" : "transparent" }}
              />
            </button>
          ))}
        </div>
      </Field>
      <Field label="Share a few words (optional)" htmlFor="rate-notes">
        <Textarea id="rate-notes" rows={4} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="What did your freelancer do well?" />
      </Field>
      <Footer SubmitIcon={Star} submitLabel="Submit rating" onSubmit={() => runAction("rate", { rating: score, notes, freelancer: freelancer.seat })} />
    </Shell>
  )
}

function CancelDialog({ order, runAction, onClose }) {
  const [reason, setReason] = useState("Changed my mind")

  return (
    <Shell title="Cancel order" onClose={onClose}>
      <Note tone="red">
        <b className="font-bold text-primary">This can&apos;t be undone.</b> Cancelling stops all work on{" "}
        <b className="font-semibold text-foreground">{order.title}</b> and releases your freelancer from the project.
      </Note>
      <Body>
        Refunds follow our{" "}
        <a href="https://theurbanwriters.com/pages/refund-and-revision-policy" target="_blank" rel="noreferrer" className="font-semibold text-primary">
          refund policy
        </a>{" "}
        and depend on how far the work has progressed. Our team will confirm any eligible amount.
      </Body>
      <Field label="Reason for cancelling">
        <Select value={reason} onValueChange={setReason}>
          <SelectTrigger className="w-full">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="Changed my mind">Changed my mind</SelectItem>
            <SelectItem value="No longer need this">No longer need this</SelectItem>
            <SelectItem value="Unhappy with progress">Unhappy with progress</SelectItem>
            <SelectItem value="Other">Other</SelectItem>
          </SelectContent>
        </Select>
      </Field>
      <Footer SubmitIcon={X} submitLabel="Cancel this order" cancelLabel="Keep order" danger onSubmit={() => runAction("cancel", { reason })} />
    </Shell>
  )
}

// Emails the customer's question to support (support@dibbly.com) with the order
// in context; replies go back to the customer by email.
function SupportDialog({ runAction, onClose }) {
  const [topic, setTopic] = useState("Question about my order")
  const [message, setMessage] = useState("")

  return (
    <Shell title="Contact support" onClose={onClose}>
      <Body>Have a question about this order? Our team typically replies by email within one business day.</Body>
      <Field label="Topic">
        <Select value={topic} onValueChange={setTopic}>
          <SelectTrigger className="w-full">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="Question about my order">Question about my order</SelectItem>
            <SelectItem value="Billing or payment">Billing or payment</SelectItem>
            <SelectItem value="Issue with a freelancer">Issue with a freelancer</SelectItem>
            <SelectItem value="Something else">Something else</SelectItem>
          </SelectContent>
        </Select>
      </Field>
      <Field label="How can we help?" htmlFor="support-message">
        <Textarea id="support-message" rows={5} value={message} onChange={(e) => setMessage(e.target.value)} placeholder="Describe your question or issue…" />
      </Field>
      <Footer
        SubmitIcon={LifeBuoy}
        submitLabel="Send to support"
        disabled={!message.trim()}
        onSubmit={() => runAction("support", { topic, message })}
      />
    </Shell>
  )
}

// Approve or decline a freelancer's deadline-extension request. The details come
// from order.pendingExtension (real OrderExtension data); declining can carry a
// reason back to the freelancer.
function ExtensionDialog({ order, runAction, onClose }) {
  const ext = order.pendingExtension
  const [reason, setReason] = useState("")

  if (!ext) {
    return (
      <Shell title="Extension request" onClose={onClose}>
        <Body>There&apos;s no pending extension on this order anymore.</Body>
        <DialogFooter>
          <DialogClose asChild>
            <Button variant="outline">Close</Button>
          </DialogClose>
        </DialogFooter>
      </Shell>
    )
  }

  const who = ext.who || "Your freelancer"

  return (
    <Shell title="Extension request" onClose={onClose}>
      <WhoLine person={{ initials: who.slice(0, 2).toUpperCase(), name: who }} sub={`${ext.position || "Freelancer"} · requested ${ext.requestedOn}`} />
      <Note tone="info">
        {who} is asking for{" "}
        {ext.additionalDays ? (
          <>
            <b className="font-bold text-primary">
              {ext.additionalDays} additional {ext.additionalDays === 1 ? "day" : "days"}
            </b>
          </>
        ) : (
          "more time"
        )}
        {ext.currentDate && ext.requestedDate ? (
          <>
            , moving the due date from <b className="font-semibold text-foreground">{ext.currentDate}</b> to{" "}
            <b className="font-semibold text-foreground">{ext.requestedDate}</b>.
          </>
        ) : ext.requestedDate ? (
          <>
            , with a requested due date of <b className="font-semibold text-foreground">{ext.requestedDate}</b>.
          </>
        ) : (
          "."
        )}
      </Note>
      {ext.reason && (
        <div className="flex flex-col gap-1.5">
          <span className="text-xs font-bold text-foreground">Their reason</span>
          <p className="rounded-[10px] border border-border bg-accent px-[15px] py-[13px] text-[13px] leading-[1.55] text-muted-foreground">{ext.reason}</p>
        </div>
      )}
      <Field label="Note if you're declining (optional)" htmlFor="ext-reason">
        <Textarea id="ext-reason" rows={3} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Let your freelancer know why…" />
      </Field>
      <DialogFooter>
        <Button variant="outline" className="gap-[7px]" onClick={() => runAction("review_extension", { approved: false, reasonDenied: reason })}>
          <X className="size-4" strokeWidth={2} />
          Decline
        </Button>
        <Button className="gap-[7px] font-bold" onClick={() => runAction("review_extension", { approved: true })}>
          <Check className="size-4" strokeWidth={2.1} />
          Approve extension
        </Button>
      </DialogFooter>
    </Shell>
  )
}

// Mark a single narration take for change. `data` carries the chapter (id +
// name) the AudioPanel opened this for; the note is stored on the take as
// customer_comment (its changes-requested signal). It is staged, not sent: the
// narrator hears about it when the customer requests changes on the order,
// which is what carries every marked take to them (AU-46).
function ChapterChangesDialog({ runAction, data, onClose }) {
  const [comment, setComment] = useState("")
  const chapterName = data?.chapterName || "this chapter"

  return (
    <Shell title="Mark this chapter for changes" onClose={onClose}>
      <Body>
        Tell your narrator what to adjust on <b className="font-semibold text-foreground">{chapterName}</b>. Mark every chapter you want re-recorded, then use <b className="font-semibold text-foreground">Request changes</b> on the order to send them — your narrator isn&apos;t told until you do.
      </Body>
      <Field label="What needs changing?" htmlFor="chapter-comment">
        <Textarea
          id="chapter-comment"
          rows={5}
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          placeholder="e.g. the pace runs a little fast from 2:10, and the character voice shifts in the second half."
        />
      </Field>
      <Footer
        SubmitIcon={RotateCcw}
        submitLabel="Save note"
        disabled={!comment.trim()}
        onSubmit={() => runAction("narration_comment", { narrationFileId: data?.chapterId, comment })}
      />
    </Shell>
  )
}

// The sample verdict posts to review_sample, not review: approving a sample
// starts recording, it does not complete the order (AU-30).
function ApproveSampleDialog({ runAction, onClose }) {
  return (
    <Shell title="Approve this sample" onClose={onClose}>
      <Note tone="info">
        Approving tells your narrator the voice and delivery are right, and they&apos;ll record the rest of your
        book. You&apos;ll review the finished chapters before the order completes.
      </Note>
      <Footer
        SubmitIcon={Check}
        submitLabel="Approve sample"
        onSubmit={() => runAction("review_sample", { approved: true })}
      />
    </Shell>
  )
}

function RequestAnotherSampleDialog({ runAction, onClose }) {
  const [notes, setNotes] = useState("")

  return (
    <Shell title="Request another sample" onClose={onClose}>
      <Note tone="info">
        Your narrator will record a new sample. Recording of the full book hasn&apos;t started, so nothing is lost.
      </Note>
      <Field label="What should your narrator change?" htmlFor="sample-notes">
        <Textarea
          id="sample-notes"
          rows={5}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Pace, tone, character voices, pronunciation — tell them what to try differently."
        />
      </Field>
      <Footer
        SubmitIcon={RotateCcw}
        submitLabel="Request another sample"
        disabled={!notes.trim()}
        onSubmit={() => runAction("review_sample", { approved: false, notes })}
      />
    </Shell>
  )
}

// The header's freelancer requests: the same review the Projects list opens,
// returning to this order once the customer accepts or declines.
function ReviewRequestsDialog({ order, onClose }) {
  return (
    <FreelancerRequestsDialog
      open
      title={order.title}
      requests={order.requestItems ?? []}
      from="order"
      onClose={onClose}
    />
  )
}

const MODALS = {
  "approve-draft": ApproveDraftDialog,
  "approve-sample": ApproveSampleDialog,
  "request-another-sample": RequestAnotherSampleDialog,
  "request-revision": RequestRevisionDialog,
  "approve-final": ApproveFinalDialog,
  "approve-design": ApproveDesignDialog,
  "review-scan": ReviewScanDialog,
  [ORDER_MODAL_KEYS.PAY_NOW]: PayNowDialog,
  [ORDER_MODAL_KEYS.REVIEW_REQUESTS]: ReviewRequestsDialog,
  hold: HoldDialog,
  reallocate: ReallocateDialog,
  tip: TipDialog,
  "manage-access": ManageAccessDialog,
  resume: ResumeDialog,
  rate: RateDialog,
  cancel: CancelDialog,
  support: SupportDialog,
  "review-extension": ExtensionDialog,
  "chapter-changes": ChapterChangesDialog,
}

// The order-detail action dialogs. `modal` is the active key (null = closed);
// each dialog submits to a Customer::Orders endpoint via `runAction`, which
// re-renders the page with the new state and closes the modal.
export default function OrderModals({ modal, modalData, onClose, order, runAction, busy = false }) {
  const Active = modal ? MODALS[modal] : null
  if (!Active) return null

  // `busy` reaches every Footer through context rather than each dialog's
  // props: the confirm button disables while its action is on the wire, so the
  // customer isn't invited to click a second time (AU-35).
  return (
    <BusyProvider busy={busy}>
      <Active order={order} runAction={runAction} data={modalData} onClose={onClose} />
    </BusyProvider>
  )
}
